import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, ArrowDown, Leaf } from "lucide-react";
import { useLang } from "../../i18n";
import { MaskedLine, FadeUp } from "../motion";

const Hero = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end lg:items-center pt-28 pb-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 z-10">
          <FadeUp delay={0.2}>
            <p data-testid="hero-eyebrow" className="text-primary/70 text-xs md:text-sm uppercase tracking-[0.25em] mb-6">
              {t("hero.eyebrow")}
            </p>
          </FadeUp>

          <h1 data-testid="hero-title" className="font-serif text-foreground tracking-tightest leading-[0.92] text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
            <MaskedLine delay={0.35}>{t("hero.l1")}</MaskedLine>
            <MaskedLine delay={0.45}>{t("hero.l2")}</MaskedLine>
            <MaskedLine delay={0.55} className="text-primary">{t("hero.l3")}</MaskedLine>
            <MaskedLine delay={0.65} className="text-coral">{t("hero.l4")}</MaskedLine>
          </h1>

          <FadeUp delay={0.9}>
            <p className="mt-8 max-w-xl text-foreground/70 text-lg font-light leading-relaxed">
              {t("hero.sub")}
            </p>
          </FadeUp>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-vital/70 hidden lg:block -z-0" />
          <div className="absolute -bottom-12 right-8 w-28 h-28 rounded-full bg-secondary/70 hidden lg:block -z-0" />
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="relative h-[420px] lg:h-[620px] overflow-hidden rounded-[2rem]"
          >
            <motion.img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/sereva/media/cadf37d1-7386-448c-8468-a55f721384bc.jpeg`}
              alt="Salade fraises, pétoncles, épinards & sel du St-Laurent"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[120%] object-cover"
            />
          </motion.div>
          <div className="absolute -bottom-6 -left-6 bg-secondary text-secondary-foreground px-6 py-4 hidden lg:block rounded-2xl z-10">
            <p className="font-serif text-2xl leading-none">Mood Food Labs</p>
            <p className="text-xs mt-1 opacity-80">Dr. Uma Naidoo · Mentorship Circle</p>
          </div>
          <div className="absolute -top-8 -right-2 lg:-right-6 w-24 h-24 rounded-full bg-vital text-vital-foreground hidden lg:flex flex-col items-center justify-center animate-floaty z-10">
            <Leaf className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
            <span className="font-serif text-sm leading-none">vitality</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-6 lg:left-10 hidden md:flex items-center gap-2 text-foreground/50 text-xs">
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="uppercase tracking-widest">Scroll</span>
      </div>
    </section>
  );
};

export default Hero;
